import React from 'react';
import styled from 'styled-components';
import { useDroppable } from '@dnd-kit/core';
import { CloseOutlined } from '@ant-design/icons';
import { Tag, Button } from 'antd';
import { resolveIcon } from './iconMap';

const PLATFORM_COLORS = {
  cipp: { bg: 'var(--odin-status-active-bg)', color: 'var(--odin-status-active-text)', label: 'M365' },
  sophos: { bg: 'var(--odin-status-failed-bg)', color: 'var(--odin-status-failed-text)', label: 'Sophos' },
  acronis: { bg: 'var(--odin-status-completed-bg)', color: 'var(--odin-status-completed-text)', label: 'Acronis' },
  autotask: { bg: 'var(--odin-status-awaiting-bg)', color: 'var(--odin-status-awaiting-text)', label: 'Autotask' },
  scalepad: { bg: 'var(--odin-code-bg)', color: 'var(--odin-text-primary)', label: 'ScalePad' },
  general: { bg: 'var(--odin-surface-hover)', color: 'var(--odin-text-secondary)', label: 'General' },
};

const TIMEFRAME_LABELS = {
  '0-3months': '0-3 Months',
  '3-6months': '3-6 Months',
  '6-12months': '6-12 Months',
  custom: 'Custom',
};

const ZoneWrapper = styled.div`
  border: 2px ${({ $isOver, $filled }) =>
    $isOver ? 'solid' : $filled ? 'solid' : 'dashed'}
    ${({ $isOver, $filled, theme }) =>
      $isOver ? (theme.colors.accent)
        : $filled ? (theme.colors.border)
        : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg || '8px'};
  background: ${({ $isOver, $filled, theme }) =>
    $isOver ? theme.colors.surfaceActive
      : $filled ? (theme.colors.surface)
      : 'transparent'};
  transition: all ${({ theme }) => theme.transitions.fast || '0.15s ease'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 120px;

  ${({ $isOver }) => $isOver && `
    transform: scale(1.02);
  `}
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 11px;
  font-style: italic;
`;

const FilledContent = styled.div`
  padding: ${({ theme }) => theme.spacing.sm || '0.5rem'};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardIcon = styled.span`
  font-size: 16px;
  flex-shrink: 0;
`;

const RemoveBtn = styled(Button)`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
`;

const TimeframeBadge = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: var(--odin-on-accent);
  background: ${({ theme }) => theme.colors.accent};
  border-radius: 10px;
  padding: 2px 8px;
  align-self: flex-start;
`;

const BudgetBadge = styled.div`
  margin-top: 4px;
  font-size: 9px;
  font-weight: 600;
  color: var(--odin-status-completed-text);
  background: var(--odin-status-completed-bg);
  border-radius: 10px;
  padding: 2px 8px;
  align-self: flex-start;
`;

const BadgesWrapper = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DropZone = ({ pageNumber, zoneNumber, placement, onRemove }) => {
  const droppableId = `zone-${pageNumber}-${zoneNumber}`;

  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: { pageNumber, zoneNumber },
  });

  const filled = !!placement;
  const platform = filled
    ? PLATFORM_COLORS[placement.platform] || PLATFORM_COLORS.general
    : null;

  return (
    <ZoneWrapper
      ref={setNodeRef}
      $isOver={isOver}
      $filled={filled}
    >
      {filled ? (
        <FilledContent>
          <RemoveBtn
            type="text"
            size="small"
            icon={<CloseOutlined style={{ fontSize: 10 }} />}
            onClick={() => onRemove(pageNumber, zoneNumber)}
            style={{ color: 'var(--odin-text-tertiary)' }}
          />
          <CardHeader>
            <CardIcon>{resolveIcon(placement.icon)}</CardIcon>
            <CardTitle>{placement.title}</CardTitle>
            {platform && (
              <Tag
                style={{
                  margin: 0,
                  fontSize: 8,
                  padding: '0 4px',
                  background: platform.bg,
                  color: platform.color,
                  border: 'none',
                }}
              >
                {platform.label}
              </Tag>
            )}
          </CardHeader>
          <BadgesWrapper>
            {placement.timeframe && (
              <TimeframeBadge>
                {placement.timeframe === 'custom'
                  ? placement.timeframe_custom || 'Custom'
                  : TIMEFRAME_LABELS[placement.timeframe] || placement.timeframe}
              </TimeframeBadge>
            )}
            {placement.variable_values?.budget && (
              <BudgetBadge>
                {placement.variable_values.budget.startsWith('£')
                  ? placement.variable_values.budget
                  : `£${placement.variable_values.budget}`}
              </BudgetBadge>
            )}
          </BadgesWrapper>
        </FilledContent>
      ) : (
        <EmptyState>
          Drop here (Zone {zoneNumber})
        </EmptyState>
      )}
    </ZoneWrapper>
  );
};

export default React.memo(DropZone);
